import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  ChevronDown,
  User,
  Users,
  AlertCircle,
  Check,
  AlertTriangle,
  ArrowUp,
  Minus,
  ArrowDown,
} from 'lucide-react';
import type { TaskType, TaskPriority, TaskCreateDto, TaskAssignee } from '@/types/task';
import { demoAssignees } from '@/data/demo-tasks';

interface ComplianceContext {
  complianceId: string;
  complianceName: string;
  assetId: string;
  assetName: string;
  assetCode?: string;
  regulationType: string;
  dueDate?: string;
  riskLevel?: string;
}

interface CreateTaskModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onTaskCreated?: (task: TaskCreateDto) => void;
  // Pre-fill context
  readonly defaultType?: TaskType;
  readonly defaultStatus?: string;
  readonly defaultAssignee?: TaskAssignee;
  readonly defaultTitle?: string;
  readonly defaultDescription?: string;
  readonly defaultDueDate?: Date;
  readonly defaultPriority?: TaskPriority;
  readonly defaultTags?: string[];
  // Compliance context
  readonly complianceContext?: ComplianceContext;
}

const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'General Task', label: 'General Task' },
  { value: 'Inspection', label: 'Inspection' },
  { value: 'Maintenance Request', label: 'Maintenance Request' },
  { value: 'Documentation Update', label: 'Documentation Update' },
  { value: 'Verification', label: 'Verification' },
  { value: 'Follow-up Action', label: 'Follow-up Action' },
  { value: 'Data Collection', label: 'Data Collection' },
];

const PRIORITIES: { value: TaskPriority; label: string; color: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'Urgent', label: 'Urgent', color: 'text-red-600', icon: AlertTriangle },
  { value: 'High', label: 'High', color: 'text-orange-600', icon: ArrowUp },
  { value: 'Medium', label: 'Medium', color: 'text-yellow-600', icon: Minus },
  { value: 'Low', label: 'Low', color: 'text-gray-600', icon: ArrowDown },
];

const AVAILABLE_TAGS = [
  { value: 'urgent', label: 'urgent' },
  { value: 'maintenance', label: 'maintenance' },
  { value: 'audit', label: 'audit' },
  { value: 'compliance', label: 'compliance' },
  { value: 'security', label: 'security' },
  { value: 'performance', label: 'performance' },
  { value: 'bug', label: 'bug' },
  { value: 'enhancement', label: 'enhancement' },
  { value: 'documentation', label: 'documentation' },
  { value: 'training', label: 'training' },
];

// Helper functions to reduce cognitive complexity in useEffect
function getInitialTitle(
  complianceContext: ComplianceContext | undefined,
  defaultTitle: string | undefined
): string {
  if (!complianceContext) {
    return defaultTitle ?? '';
  }
  return defaultTitle ?? `${complianceContext.regulationType} - ${complianceContext.assetName}`;
}

function getInitialDescription(
  complianceContext: ComplianceContext | undefined,
  defaultDescription: string | undefined
): string {
  if (!complianceContext) {
    return defaultDescription ?? '';
  }
  const assetCodeSuffix = complianceContext.assetCode ? ' (' + complianceContext.assetCode + ')' : '';
  const contextDescription = [
    `Compliance Item: ${complianceContext.complianceName}`,
    `Asset: ${complianceContext.assetName}${assetCodeSuffix}`,
    `Regulation: ${complianceContext.regulationType}`,
    complianceContext.dueDate ? `Due Date: ${new Date(complianceContext.dueDate).toLocaleDateString()}` : '',
    complianceContext.riskLevel ? `Risk Level: ${complianceContext.riskLevel}` : '',
    '',
    defaultDescription ?? 'Please complete the required compliance action.',
  ].filter(Boolean).join('\n');
  return contextDescription;
}

function getInitialPriority(
  complianceContext: ComplianceContext | undefined,
  defaultPriority: TaskPriority | undefined
): TaskPriority {
  if (!complianceContext?.riskLevel) {
    return defaultPriority ?? 'Medium';
  }
  const riskToPriority: Record<string, TaskPriority> = {
    'Critical': 'Urgent',
    'High': 'High',
    'Medium': 'Medium',
    'Low': 'Low',
  };
  return riskToPriority[complianceContext.riskLevel] ?? defaultPriority ?? 'Medium';
}

function getInitialDueDate(
  complianceContext: ComplianceContext | undefined,
  defaultDueDate: Date | undefined
): Date {
  if (complianceContext?.dueDate) {
    return new Date(complianceContext.dueDate);
  }
  if (defaultDueDate) {
    return defaultDueDate;
  }
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date;
}

function getInitialTags(
  complianceContext: ComplianceContext | undefined,
  defaultTags: string[] | undefined
): string[] {
  if (complianceContext) {
    return defaultTags ?? ['compliance'];
  }
  return defaultTags ?? [];
}

export function CreateTaskModal({
  open,
  onOpenChange,
  onTaskCreated,
  defaultType,
  defaultAssignee,
  defaultTitle,
  defaultDescription,
  defaultDueDate,
  defaultPriority,
  defaultTags,
  complianceContext,
}: Readonly<CreateTaskModalProps>) {
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TaskType>(defaultType ?? 'General Task');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [assigneeType, setAssigneeType] = useState<'user' | 'group'>('user');
  const [selectedAssignee, setSelectedAssignee] = useState<TaskAssignee | undefined>(defaultAssignee);
  const [tags, setTags] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7); // 1 week from today
    return date;
  });

  // UI state
  const [assigneeSearchOpen, setAssigneeSearchOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Character count
  const titleCharCount = title.length;
  const titleMaxChars = 200;
  const descriptionCharCount = description.length;
  const descriptionMaxChars = 2000;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      return;
    }
    
    setTitle(getInitialTitle(complianceContext, defaultTitle));
    setDescription(getInitialDescription(complianceContext, defaultDescription));
    setPriority(getInitialPriority(complianceContext, defaultPriority));
    setDueDate(getInitialDueDate(complianceContext, defaultDueDate));
    setTags(getInitialTags(complianceContext, defaultTags));
    setType(defaultType ?? 'General Task');
    setAssigneeType('user');
    setSelectedAssignee(defaultAssignee);
    setErrors({});
    setHasChanges(false);
  }, [open, defaultType, defaultAssignee, defaultTitle, defaultDescription, defaultDueDate, defaultPriority, defaultTags, complianceContext]);

  // Track changes
  useEffect(() => {
    const hasAnyChanges = title.length > 0 || description.length > 0 || tags.length > 0;
    setHasChanges(hasAnyChanges);
  }, [title, description, tags]);

  // Filter assignees based on type and search
  const filteredAssignees = demoAssignees
    .filter((a) => a.type === assigneeType)
    .filter((a) => {
      if (!assigneeSearch) return true;
      const searchLower = assigneeSearch.toLowerCase();
      return (
        a.name.toLowerCase().includes(searchLower) ||
        a.email.toLowerCase().includes(searchLower)
      );
    });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (title.trim().length < 2) {
      newErrors.title = 'Title must be at least 2 characters';
    }
    if (title.length > titleMaxChars) {
      newErrors.title = `Title must be less than ${titleMaxChars} characters`;
    }
    if (description.length > descriptionMaxChars) {
      newErrors.description = `Description must be less than ${descriptionMaxChars} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowUnsavedWarning(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const taskData: TaskCreateDto = {
      title,
      description: description ?? undefined,
      type: complianceContext ? 'Inspection' : type, // Set to Inspection if from compliance
      priority,
      assigneeId: selectedAssignee?.id,
      tags: tags.length > 0 ? tags : undefined,
      dueDate: dueDate.toISOString(),
      // Add compliance link if context provided
      links: complianceContext ? [
        {
          type: 'asset' as const,
          entityId: complianceContext.assetId,
          entityName: complianceContext.assetName,
          metadata: {
            complianceId: complianceContext.complianceId,
            complianceName: complianceContext.complianceName,
            regulationType: complianceContext.regulationType,
            assetCode: complianceContext.assetCode,
          },
        },
      ] : undefined,
    };

    onTaskCreated?.(taskData);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-[950px] max-h-[90vh] overflow-y-auto bg-background">
          <DialogHeader>
            <DialogTitle>Create Work Item</DialogTitle>
            <DialogDescription>
              {complianceContext ? (
                <span className="flex flex-col gap-1">
                  <span>Create a task for compliance action</span>
                  <span className="text-xs text-muted-foreground">
                    This task will be linked to: {complianceContext.complianceName}
                  </span>
                </span>
              ) : (
                'Create a new task with all the necessary details'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Two Column Layout - 60/40 split */}
            <div className="grid grid-cols-[60%_40%] gap-6 pr-8">
              {/* Left Column */}
              <div className="space-y-5">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title" className="flex items-center gap-1">
                    Title <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="What needs to be done?"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={titleMaxChars}
                    className={cn(errors.title && 'border-red-600')}
                    autoFocus
                  />
                  <div className="flex items-center justify-between text-xs min-h-[20px]">
                    {errors.title ? (
                      <span className="text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.title}
                      </span>
                    ) : (
                      <span />
                    )}
                    {titleCharCount > titleMaxChars * 0.8 && (
                      <span
                        className={cn(
                          'text-muted-foreground',
                          titleCharCount > titleMaxChars * 0.95 && 'text-orange-600 font-medium'
                        )}
                      >
                        {titleMaxChars - titleCharCount} characters remaining
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Add a detailed description..."
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    maxLength={descriptionMaxChars}
                    className={cn(
                      'min-h-[120px] bg-white dark:bg-background',
                      errors.description && 'border-red-600'
                    )}
                  />
                  <div className="flex items-center justify-between text-xs min-h-[20px]">
                    {errors.description ? (
                      <span className="text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.description}
                      </span>
                    ) : (
                      <span />
                    )}
                    {descriptionCharCount > descriptionMaxChars * 0.8 && (
                      <span
                        className={cn(
                          'text-muted-foreground',
                          descriptionCharCount > descriptionMaxChars * 0.95 && 'text-orange-600 font-medium'
                        )}
                      >
                        {descriptionMaxChars - descriptionCharCount} characters remaining
                      </span>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags</Label>
                  <MultiSelect
                    options={AVAILABLE_TAGS}
                    placeholder="Select or create tags..."
                    emptyMessage="No tags found."
                    value={tags}
                    onValueChange={setTags}
                    maxDisplay={5}
                    allowCustom={true}
                    customLabel="Create tag"
                  />
                  <p className="text-xs text-muted-foreground">
                    Select from predefined tags or create custom ones
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                {/* Type */}
                <div className="space-y-2">
                  <Label htmlFor="type" className="flex items-center gap-1">
                    Type <span className="text-red-600">*</span>
                  </Label>
                  <Select value={type} onValueChange={(value: string) => setType(value as TaskType)}>
                    <SelectTrigger id="type" className="bg-white dark:bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assignee */}
                <div className="space-y-2">
                  <Label>Assign to</Label>
                  <div className="space-y-3">
                    {/* User/Group Toggle */}
                    <ToggleGroup
                      type="single"
                      value={assigneeType}
                      onValueChange={(value: string) => {
                        if (value) {
                          setAssigneeType(value as 'user' | 'group');
                          setSelectedAssignee(undefined);
                          setAssigneeSearch('');
                        }
                      }}
                      className="justify-start"
                    >
                      <ToggleGroupItem value="user" className="gap-2">
                        <User className="h-4 w-4" />
                        User
                      </ToggleGroupItem>
                      <ToggleGroupItem value="group" className="gap-2">
                        <Users className="h-4 w-4" />
                        Group
                      </ToggleGroupItem>
                    </ToggleGroup>

                    {/* Assignee Search */}
                    <Popover open={assigneeSearchOpen} onOpenChange={setAssigneeSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between bg-white dark:bg-background"
                        >
                          {selectedAssignee ? (
                            <div className="flex items-center gap-2">
                              {selectedAssignee.type === 'user' ? (
                                <>
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={selectedAssignee.avatar} />
                                    <AvatarFallback>
                                      {selectedAssignee.name
                                        .split(' ')
                                        .map((n: string) => n[0])
                                        .join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span>{selectedAssignee.name}</span>
                                </>
                              ) : (
                                <>
                                  <Users className="h-4 w-4 text-purple-600" />
                                  <span>{selectedAssignee.name}</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              Select {assigneeType === 'user' ? 'user' : 'group'}...
                            </span>
                          )}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder={`Search ${assigneeType === 'user' ? 'users' : 'groups'}...`}
                            value={assigneeSearch}
                            onValueChange={setAssigneeSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No {assigneeType} found.</CommandEmpty>
                            <CommandGroup>
                              {filteredAssignees.map((assignee) => (
                                <CommandItem
                                  key={assignee.id}
                                  value={assignee.name}
                                  onSelect={() => {
                                    setSelectedAssignee(assignee);
                                    setAssigneeSearchOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      selectedAssignee?.id === assignee.id
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                  {assignee.type === 'user' ? (
                                    <div className="flex items-center gap-2">
                                      <Avatar className="h-6 w-6">
                                        <AvatarImage src={assignee.avatar} />
                                        <AvatarFallback>
                                          {assignee.name
                                            .split(' ')
                                            .map((n: string) => n[0])
                                            .join('')}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex flex-col">
                                        <span className="text-sm">{assignee.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                          {assignee.email}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <Users className="h-4 w-4 text-purple-600" />
                                      <div className="flex flex-col">
                                        <span className="text-sm">{assignee.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                          {assignee.email}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={priority} onValueChange={(value: string) => setPriority(value as TaskPriority)}>
                    <SelectTrigger id="priority" className="bg-white dark:bg-background">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const Icon = PRIORITIES.find(p => p.value === priority)?.icon;
                            return Icon ? <Icon className="h-4 w-4" /> : null;
                          })()}
                          <span className={PRIORITIES.find(p => p.value === priority)?.color}>
                            {PRIORITIES.find(p => p.value === priority)?.label}
                          </span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => {
                        const Icon = p.icon;
                        return (
                          <SelectItem key={p.value} value={p.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span className={p.color}>{p.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning */}
      <AlertDialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              Discard Changes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in this task. Are you sure you want to discard
              them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUnsavedWarning(false)}>
              Stay
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
